import React, { useEffect, useState, useMemo } from 'react';
import { Form, Input, Button, Typography, message, Divider, Card } from 'antd';
import { SaveOutlined, UserOutlined } from '@ant-design/icons';
import CommonForm from '../../../global/form';
import CommonSelect from '../../../global/select';
import { useGenericMutation } from '../../../../queries/use-generic-mutation';
import { useGenericQuery } from '../../../../queries/use-generic-query';
import { QUERY_KEYS } from '../../../../lib/query-constants';
import SOURCES from '../../../../utils/constants';
import { cn } from '../../../../utils/cn';
import Spinner from '../../../global/spinner';
import { capitalizeFirstLetter } from '../../../../utils/functions';

const { Title, Text } = Typography;

const EnhancedConversate = ({ destinations, activeDestination, btnClassName }) => {
    const [form] = Form.useForm();

    const [accounts, setAccounts] = useState([]);
    const [inboxes, setInboxes] = useState([]);
    const [agents, setAgents] = useState([]);
    const [selectedAccount, setSelectedAccount] = useState(null);

    const email = Form.useWatch('email', form);
    const password = Form.useWatch('password', form);

    const DESTINATION_CONVERSATE =
        SOURCES.DESTINATION_CONVERSATE || 'conversate';

    /* ----------------------------- QUERIES ----------------------------- */

    const {
        data: accountsData,
        isFetching: accountsLoading,
        refetch: refetchAccounts,
    } = useGenericQuery({
        queryKey: [QUERY_KEYS.conversate.all, 'accounts', email],
        endpoint: 'integration/conversate-accounts',
        queryParams: {
            destinationAccountEmail: email,
            destinationAccountPassword: password,
        },
        enabled: false,
    });

    const {
        data: inboxesData,
        isFetching: inboxesLoading,
        refetch: refetchInboxes,
    } = useGenericQuery({
        queryKey: [
            QUERY_KEYS.conversate.all,
            'inboxes',
            selectedAccount,
            email,
        ],
        endpoint: 'integration/conversate-inboxes',
        queryParams: {
            destinationAccountEmail: email,
            destinationAccountPassword: password,
            accountId: selectedAccount,
        },
        enabled: false,
    });

    const {
        data: agentsData,
        isFetching: agentsLoading,
        refetch: refetchAgents,
    } = useGenericQuery({
        queryKey: [
            QUERY_KEYS.conversate.all,
            'agents',
            selectedAccount,
            email,
        ],
        endpoint: 'integration/conversate-agents',
        queryParams: {
            destinationAccountEmail: email,
            destinationAccountPassword: password,
            accountId: selectedAccount,
        },
        enabled: true,
    });

    /* ---------------------------- MUTATION ----------------------------- */

    const { mutate: saveConversate, isPending: isSaving } =
        useGenericMutation({
            endpoint: 'integration',
            method: 'POST',
            onSuccess: () =>
                message.success(
                    'Conversate configuration saved successfully'
                ),
        });

    /* --------------------------- DATA MAPPING --------------------------- */

    useEffect(() => {
        if (accountsData) {
            setAccounts(
                accountsData.map((acc) => ({
                    label: capitalizeFirstLetter(acc.name),
                    value: acc.id,
                }))
            );
        }
    }, [accountsData]);

    useEffect(() => {
        if (inboxesData) {
            setInboxes(
                inboxesData.map((i) => ({
                    label: capitalizeFirstLetter(i.name),
                    value: i.id,
                }))
            );
        }
    }, [inboxesData]);

    useEffect(() => {
        if (agentsData) {
            setAgents(
                agentsData.map((a) => ({
                    label: capitalizeFirstLetter(a.name || a.title),
                    value: a.id || a.key,
                }))
            );
        }
    }, [agentsData]);

    /* ------------------------ PREFILL FROM API ------------------------- */

    useEffect(() => {
        if (!destinations) return;

        const config = destinations.find(
            (d) => d.integrationCompany === DESTINATION_CONVERSATE
        );


        if (!config) return;

        form.setFieldsValue({
            email: config.destinationAccountEmail,
            password: config.destinationAccountPassword,
            sendLeadsLimit: config.leadSentConfig?.sentLimit || 0,
            initialMessage: config.extraInfo?.initialMessage || '',
            inbox: config.locations?.[0],
            agent: config.extraInfo?.agent,
            account: config.extraInfo?.account_id,
        });

        if (config.conversateAccounts) {
            setAccounts(
                config.conversateAccounts.map((a) => ({
                    label: a.label,
                    value: a.value,
                }))
            );
        }

        if (config.inboxProvider) {
            setInboxes(
                config.inboxProvider.map((i) => ({
                    label: i.label,
                    value: i.value,
                }))
            );
        }

        if (config.agentProvider) {
            setAgents(
                config.agentProvider.map((a) => ({
                    label: a.label,
                    value: a.value,
                }))
            );
        }

        setSelectedAccount(config.extraInfo?.account_id || null);
    }, [destinations, form]);

    /* ---------------------------- HANDLERS ----------------------------- */

    // Matches old getAccounts()
    const handleGetAccounts = () => {
        const { email, password } = form.getFieldsValue([
            'email',
            'password',
        ]);

        if (!email || !password) {
            message.warning('Please enter Email and Password first');
            return;
        }

        refetchAccounts();
    };

    // Matches old fetchInboxAgent logic
    const handleAccountChange = (accountId) => {
        setSelectedAccount(accountId);
        form.setFieldValue('account', accountId);

        const { email, password } = form.getFieldsValue([
            'email',
            'password',
        ]);

        if (!email || !password || !accountId) return;

        // Fetch inbox + agent together (same as old component)
        refetchInboxes({
            destinationAccountEmail: email,
            destinationAccountPassword: password,
            accountId: accountId,
        });
        refetchAgents({
            destinationAccountEmail: email,
            destinationAccountPassword: password,
            accountId: accountId,
        });
    };

    /* ----------------------------- SUBMIT ------------------------------ */

    const onFinish = (values) => {
        const payload = {
            destinationAccountEmail: values.email,
            destinationAccountPassword: values.password,
            integrationCompany: DESTINATION_CONVERSATE,
            sendLeadsLimit: values.sendLeadsLimit,
            locations: [values.inbox],
            extraInfo: {
                agent: values.agent,
                initialMessage: values.initialMessage,
                account_id: values.account,
            },
            inboxProvider: inboxes,
            agentProvider: agents,
            conversateAccounts: accounts,
        };

        saveConversate(payload);
    };

    /* ---------------------------- FORM DATA ---------------------------- */

    const formData = useMemo(
        () => [
            {
                inputType: SOURCES.FORMINPUTTYPES.INPUT,
                label: 'Account Email',
                name: 'email',
                placeholder: 'Conversate Email',
                type: 'email',
                required: true,
                span: 12,
            },
            {
                inputType: SOURCES.FORMINPUTTYPES.INPUT,
                label: 'Account Password',
                name: 'password',
                placeholder: 'Conversate Password',
                type: 'password',
                required: true,
                span: 12,
            },
            {
                inputType: SOURCES.FORMINPUTTYPES.CUSTOM,
                span: 24,
                component: (
                    <div className="flex gap-4">
                        <Button
                            icon={
                                accountsLoading ? null : <UserOutlined />
                            }
                            onClick={handleGetAccounts}
                            className="h-10 hover:!border-primary hover:!text-primary"
                        >
                            {accountsLoading && (
                                <Spinner className="!border-primary" />
                            )}
                            Get Account(s)
                        </Button>

                        <Form.Item
                            name="account"
                            className="flex-1 !mb-0"
                            required
                        >
                            <CommonSelect
                                placeholder="Select Conversate Account"
                                options={accounts}
                                onChange={handleAccountChange}
                            />
                        </Form.Item>
                    </div>
                ),
            },
            {
                inputType: SOURCES.FORMINPUTTYPES.SELECT,
                label: 'Inbox',
                name: 'inbox',
                placeholder: 'Select Inbox',
                options: inboxes,
                required: true,
                span: 12,
                loading: inboxesLoading,
            },
            {
                inputType: SOURCES.FORMINPUTTYPES.SELECT,
                label: 'Agent',
                name: 'agent',
                placeholder: 'Select Agent',
                options: agents,
                required: true,
                span: 12,
                loading: agentsLoading,
            },
            {
                inputType: SOURCES.FORMINPUTTYPES.INPUT,
                label: 'Initial Lead Send Limit',
                name: 'sendLeadsLimit',
                placeholder: 'Leads limit',
                type: 'number',
                span: 12,
            },
            {
                inputType: SOURCES.FORMINPUTTYPES.INPUT,
                type: 'textarea',
                label: 'Initial Message (Optional)',
                name: 'initialMessage',
                placeholder: 'Enter welcome message...',
                rows: 4,
                span: 24,
            },
        ],
        [
            accounts,
            inboxes,
            agents,
            accountsLoading,
            inboxesLoading,
            agentsLoading,
        ]
    );

    /* ----------------------------- RENDER ------------------------------ */

    return (
        <div className="animate-in fade-in duration-500">
            <div className="flex items-center gap-4 mb-8">
                <div className="size-16 rounded-2xl bg-white shadow-sm border border-gray-100 flex items-center justify-center p-3">
                    <img
                        src={activeDestination.icon}
                        alt={activeDestination.title}
                        className="w-full h-full object-contain"
                    />
                </div>
                <div>
                    <Title level={3} className="!mb-1">
                        {activeDestination.title} Configuration
                    </Title>
                    <Text type="secondary">
                        Connect your Conversate account and manage message
                        routing.
                    </Text>
                </div>
            </div>

            <Card className="rounded-2xl border-none shadow-sm transition-all">
                <CommonForm
                    form={form}
                    data={formData}
                    onFinish={onFinish}
                    layoutType="vertical"
                />

                <Divider className="mb-4 border-gray-100" />

                <div className="flex justify-start">
                    <Button
                        type="primary"
                        size="large"
                        icon={isSaving ? null : <SaveOutlined />}
                        onClick={() => form.submit()}
                        className={cn(btnClassName)}
                    >
                        {isSaving && (
                            <Spinner className="!border-white" />
                        )}
                        Save Configuration
                    </Button>
                </div>
            </Card>
        </div>
    );
};

export default EnhancedConversate;
